import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { VideoPlayerDialog, VideoTestDialog } from "../components/user/VideoDialogs";
import { AlertManager, useAlerts } from "../components/user/AlertManager";
import { Video, useVideoData } from "../hooks/useVideoData";
import { usePlaybackWindow } from "../hooks/usePlaybackWindow";
import { useSeekGuard } from "../hooks/useSeekGuard";
import { useDialog } from "../hooks/useDialog";
import { GridColDef } from "@mui/x-data-grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { LightTooltip } from "../components/LightToolTip";
import { TableLayout } from "../components/layout/TableLayout";
import { useNotify } from "../components/NotificationProvider";
import Typography from "@mui/material/Typography";
import { formatDate } from "../util/datetime";

interface VideoListProps {
  id: string;
  lectureName: string;
}

// 拡張されたビデオデータ型
interface EnhancedVideo extends Video {
  statement: string;
  videoEndDate: string;
  testStatement: string;
  testCreatedDate: string;
}

export default function VideoList({ id, lectureName }: VideoListProps) {
  // Custom hooks
  // 初回視聴(未完了)中のシーク(先送り)を禁止するガード。
  // heartbeat に現在の再生位置を載せるため useVideoData より先に生成する。
  const seekGuard = useSeekGuard();

  const {
    videos,
    statements,
    videoEndDates,
    testStatements,
    testCreatedDates,
    testContents,
    videoUrl,
    fetchVideos,
    getVideoStatement,
    getVideoUrl,
    recordVideoStart,
    recordVideoEnd,
    recordVideoStop,
    clearVideoSession,
    submitTestResult
  } = useVideoData({ getPlayedSeconds: seekGuard.getPlayedSeconds });

  const { fetchPlaybackWindow } = usePlaybackWindow();
  const videoDialog = useDialog();
  const testDialog = useDialog();
  const alertManager = useAlerts();
  const notify = useNotify();

  // Video dialog state
  const [selectedVideo, setSelectedVideo] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Test answer state
  const [testAnswer, setTestAnswer] = useState("");

  // Fetch videos on component mount
  useEffect(() => {
    fetchVideos(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 深夜ブロック境界(22:00)で再生を強制停止するためのタイマー
  const forceStopTimerRef = useRef<number | null>(null);

  const clearForceStopTimer = () => {
    if (forceStopTimerRef.current !== null) {
      window.clearTimeout(forceStopTimerRef.current);
      forceStopTimerRef.current = null;
    }
  };

  // アンマウント時にタイマーを残さない
  useEffect(() => clearForceStopTimer, []);

  // Video player handlers
  const handleOpenVideo = async (videoName: string, fileName: string, videoId: string) => {
    // 深夜時間帯(22:00〜5:00 JST)は再生させない。判定はサーバ時刻ベース。
    // 到達位置は開くたびにサーバから取り直す(一覧のキャッシュはページ読み込み時の
    // ものなので、モーダルを閉じてすぐ開き直したときに古い位置へ戻らないように)。
    const [playbackWindow, statement] = await Promise.all([
      fetchPlaybackWindow(),
      getVideoStatement(videoId),
    ]);
    if (playbackWindow?.blocked) {
      notify(playbackWindow.message);
      return;
    }

    setSelectedVideo({ id: videoId, name: videoName });
    // サーバに記録済みの最大再生位置から先送り禁止の追跡を始める
    // (前回までに視聴済みの範囲へは先送りできる)。
    // ガードは初回視聴(未完了)のときだけ有効。取得失敗時は安全側(有効)に倒す。
    seekGuard.reset(statement?.maxPlayedSeconds ?? 0, !statement?.statement);
    getVideoUrl(fileName);
    videoDialog.openDialog();

    // 再生中にブロック開始時刻(22:00)へ達したら強制停止する。
    // +1秒の余裕はサーバ側が確実にブロック時間帯に入ってから停止するため。
    // 停止時に stop は送らない(境界以降のheartbeatはサーバが403で拒否するため、
    // 送らなくても視聴時間が過大に記録されることはない)。
    if (playbackWindow?.secondsUntilChange != null) {
      clearForceStopTimer();
      forceStopTimerRef.current = window.setTimeout(() => {
        clearVideoSession();
        videoDialog.closeDialog();
        notify(playbackWindow.message);
      }, (playbackWindow.secondsUntilChange + 1) * 1000);
    }
  };

  const handleCloseVideo = async () => {
    if (selectedVideo) {
      clearForceStopTimer();
      await recordVideoStop();
      videoDialog.closeDialog();
    }
  };

  const handleVideoEnd = async () => {
    if (selectedVideo) {
      await recordVideoEnd();
      // 視聴完了した瞬間から自由にシークできるようにする
      seekGuard.setEnabled(false);
    }
  };

  const handleVideoPlay = async () => {
    if (selectedVideo) {
      await recordVideoStart(selectedVideo.id);
    }
  };

  const handleVideoPause = async () => {
    if (selectedVideo) {
      await recordVideoStop();
    }
  };

  const handleVideoError = (error: any) => {
    if (error && error.code === 2) {
      notify("動画のセッションが切れました。");
      clearForceStopTimer();
      videoDialog.closeDialog();
    }
    console.log("Error has Occurred:", error);
  };

  // Test dialog handlers
  const handleOpenTest = async (videoName: string, status: string, videoId: string) => {
    // 深夜時間帯は受験もブロックする(強制力はサーバ側の403が持つ)
    const playbackWindow = await fetchPlaybackWindow();
    if (playbackWindow?.blocked) {
      notify(playbackWindow.message);
      return;
    }

    setSelectedVideo({ id: videoId, name: videoName });
    const savedContent = testContents[videoId] || "";
    setTestAnswer(savedContent);
    testDialog.openDialog();
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTestAnswer(event.target.value);
  };

  const handleCloseTest = () => {
    testDialog.closeDialog();
    setTestAnswer("");
    alertManager.closeAlert();
  };

  const handleTestSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedVideo) return;

    const formData = new FormData(event.currentTarget);
    const answer = formData.get("answer") as string;

    const result = await submitTestResult(selectedVideo.id, answer);

    if (result.success) {
      if (result.status === "new") {
        alertManager.setAlert("successAlert", true);
      } else if (result.status === "update") {
        alertManager.setAlert("doneAlert", true);
      }
    }
  };

  // データグリッド用のビデオデータを作成
  const enhancedVideos: EnhancedVideo[] = videos.map(video => ({
    ...video,
    statement: statements[video.id] || "未完了",
    videoEndDate: videoEndDates[video.id] || "-",
    testStatement: testStatements[video.id] || "未受験",
    testCreatedDate: testCreatedDates[video.id] || "-"
  }));

  // カラム定義
  const columns = getVideoColumns(handleOpenVideo, handleOpenTest);

  return (
    <>
      <TableLayout
        header={{
          title: lectureName,
          buttons: []
        }}
        table={{
          data: enhancedVideos,
          columns: columns,
          getRowKey: (video) => video.id,
          ariaLabel: "video list table"
        }}
        footer={{
          title: "【注意】",
          content: (
            <Typography component="div">
              ・上の動画から順に視聴してください。
              <br />
              ・動画を見終わらないと小テストは受験できません。
              <br />
              ・1回目の視聴では、まだ見ていない箇所への早送り（スキップ）はできません。視聴完了後は自由に移動できます。
              <br />
              ・視聴を中断した動画は、次に開いたとき続きから再生されます。
            </Typography>
          )
        }}
      />
      {/* Video Player Dialog */}
      <VideoPlayerDialog
        open={videoDialog.isOpen}
        onClose={handleCloseVideo}
        videoName={selectedVideo?.name || ""}
        videoUrl={videoUrl}
        onEnded={handleVideoEnd}
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onError={handleVideoError}
        playerRef={seekGuard.playerRef}
        onReady={seekGuard.handleReady}
        onProgress={seekGuard.handleProgress}
      />

      {/* Test Dialog */}
      <VideoTestDialog
        open={testDialog.isOpen}
        onClose={handleCloseTest}
        videoName={selectedVideo?.name || ""}
        text={testAnswer}
        onTextChange={handleTextChange}
        onSubmit={handleTestSubmit}
        isUpdate={selectedVideo ? testStatements[selectedVideo.id] === "受験済" : false}
      />

      {/* Alerts */}
      <AlertManager alerts={alertManager.getAlertConfigs()} />
    </>
  );
}

/**
 * ビデオ一覧表示用のカラム定義
 */
function getVideoColumns(
  handleOpenVideo: (videoName: string, fileName: string, videoId: string) => void,
  handleOpenTest: (videoName: string, statement: string, videoId: string) => void
): GridColDef<EnhancedVideo>[] {
  return [
    {
      field: "videoName",
      headerName: "動画名",
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.videoName}
        </Typography>
      )
    },
    {
      field: "statement",
      headerName: "視聴状況",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <LightTooltip
          title={
            <React.Fragment>
              視聴日: {params.row.videoEndDate !== "-" ? formatDate(params.row.videoEndDate, true) : "-"}
            </React.Fragment>
          }
          placement="right"
        >
          <Typography>{params.row.statement}</Typography>
        </LightTooltip>
      )
    },
    {
      field: "watch",
      headerName: "視聴",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="play"
          size="large"
          onClick={() => handleOpenVideo(params.row.videoName, params.row.fileName, params.row.id)}
        >
          <PlayCircleIcon fontSize="medium" />
        </IconButton>
      )
    },
    {
      field: "testStatement",
      headerName: "回答状況",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <LightTooltip
          title={
            <React.Fragment>
              回答日: {params.row.testCreatedDate !== "-" ? formatDate(params.row.testCreatedDate, true) : "-"}
            </React.Fragment>
          }
          placement="right"
        >
          <Typography>{params.row.testStatement}</Typography>
        </LightTooltip>
      )
    },
    {
      field: "test",
      headerName: "小テスト",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => handleOpenTest(params.row.videoName, params.row.statement, params.row.id)}
          disabled={params.row.statement !== "完了"}
          sx={{
            borderRadius: "20px",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
        >
          回答する
        </Button>
      )
    }
  ];
}
